import React, { useState } from 'react'
import { makeStyles } from '@material-ui/core/styles'
import {
    Box, Button, Card, CardHeader, CardContent, Collapse, Typography,
    Grid, FormControl, FormGroup, FormLabel, OutlinedInput,
    Select, MenuItem, IconButton
} from '@material-ui/core'
import DeleteIcon from '@material-ui/icons/Delete'
import ExpandMoreIcon from '@material-ui/icons/ExpandMore'
import Loader from 'components/general/Loader'
import ProviderAPI from 'config/api/ProviderAPI'
import { onError } from 'config/lib/errorLib'
import { NotificationManager } from 'react-notifications'
import componentStyles from 'assets/theme/views/admin/react-bs-table.js'

const useStyles = makeStyles(componentStyles)

function formatIrishPhone(phone) {
    if (!phone) return null
    const raw = String(phone).trim()
    if (!raw) return null
    const digits = raw.replace(/\D/g, '')
    if (!digits) return null
    if (raw.startsWith('+')) return '+' + digits
    if (digits.startsWith('353')) return '+' + digits
    return '+353' + digits.replace(/^0+/, '')
}

function isValidIrishPhone(phone) {
    const formatted = formatIrishPhone(phone)
    if (!formatted) return false
    // After +353, expect 7-9 digits (Irish mobile/landline)
    const afterPrefix = formatted.replace('+353', '')
    return afterPrefix.length >= 7 && afterPrefix.length <= 9
}

function SmsNotificationsCard({ companyDetails, updateField, updateCompany }) {
    const classes = { ...useStyles() }
    const [open, setOpen] = useState(true)
    const [loading, setLoading] = useState(false)
    const [newPhone, setNewPhone] = useState('')
    const api = new ProviderAPI()

    const profileDetails = companyDetails.profileDetails || {}
    const smsNotificationNumbers = profileDetails.smsNotificationNumbers || []
    const deliveryMethod = companyDetails.deliveryDetails?.deliveryMethod || ''
    const deliveryMethodLower = deliveryMethod.toLowerCase()
    const hasDeliveryMethod = !!deliveryMethod

    const SMS_LIMIT = 160
    const smsPreview = `A new lead ([Name]) has been assigned to you. Check your ${deliveryMethodLower || '[delivery method]'} for full details. Call them on [phone]`

    function addNumber() {
        const trimmed = newPhone.trim()
        if (!trimmed) return
        const formatted = formatIrishPhone(trimmed)
        if (!formatted || !isValidIrishPhone(trimmed)) {
            NotificationManager.error('Please enter a valid Irish phone number')
            return
        }
        if (smsNotificationNumbers.some(item => item.phone === formatted)) {
            NotificationManager.error('This number has already been added')
            return
        }
        const updated = [...smsNotificationNumbers, { phone: formatted, enabled: false }]
        updateField('profileDetails.smsNotificationNumbers', updated)
        setNewPhone('')
    }

    function removeNumber(index) {
        const updated = smsNotificationNumbers.filter((_, i) => i !== index)
        updateField('profileDetails.smsNotificationNumbers', updated)
    }

    function toggleEnabled(index, value) {
        if (value && !hasDeliveryMethod) {
            NotificationManager.error('A delivery method must be set before enabling SMS notifications')
            return
        }
        const updated = smsNotificationNumbers.map((item, i) =>
            i === index ? { ...item, enabled: value } : item
        )
        updateField('profileDetails.smsNotificationNumbers', updated)
    }

    async function saveDetails() {
        setLoading(true)
        try {
            const updatedDetails = {
                ...profileDetails,
                smsNotificationNumbers
            }
            const updatedCompany = await api.updateProfileDetails(companyDetails.companyId, updatedDetails)
            updateCompany(updatedCompany)
            NotificationManager.success('SMS Notification settings saved')
        } catch (e) {
            onError(e)
        }
        setLoading(false)
    }

    return (
        <Card>
            <CardHeader
                subheader={
                    <Box display='flex' style={{ justifyContent: 'space-between' }}>
                        <Box>
                            <Box component={Typography} variant='h2' marginBottom='0!important'>
                                <Box component='span'>SMS Notifications (to company)</Box>
                            </Box>
                            <Box
                                component='p'
                                fontSize='.875rem'
                                marginBottom='0'
                                marginTop='0'
                                lineHeight='1.7'
                                fontWeight='300'
                            >
                                Manage phone numbers that receive SMS notifications when a new lead is assigned
                            </Box>
                        </Box>
                    </Box>
                }
                onClick={() => setOpen(o => !o)}
                style={{ cursor: 'pointer' }}
                action={<ExpandMoreIcon style={{ transform: open ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s', marginTop: 4 }} />}
                classes={{ root: classes.cardHeaderRoot }}
            />
            <Collapse in={open}>
                <CardContent>
                    <Grid container spacing={3}>
                        {/* Add new number */}
                        <Grid item xs={12}>
                            <FormGroup>
                                <FormLabel>Add Phone Number</FormLabel>
                                <Box display='flex' alignItems='center' style={{ gap: '0.5rem' }}>
                                    <OutlinedInput
                                        value='+353'
                                        disabled
                                        style={{ width: 70, color: '#8898aa', fontWeight: 500 }}
                                    />
                                    <FormControl variant='filled' component={Box} width='100%' maxWidth='250px'>
                                        <OutlinedInput
                                            autoComplete='off'
                                            type='text'
                                            placeholder='(086) 000 1111'
                                            value={newPhone}
                                            onChange={(e) => setNewPhone(e.target.value)}
                                            onKeyDown={(e) => { if (e.key === 'Enter') addNumber() }}
                                        />
                                    </FormControl>
                                    <Button
                                        variant='outlined'
                                        color='primary'
                                        onClick={addNumber}
                                        disabled={!newPhone.trim()}
                                    >
                                        Add
                                    </Button>
                                </Box>
                                <Typography variant='caption' style={{ marginTop: 4, display: 'block', color: '#8898aa' }}>
                                    New numbers are disabled by default. Enable them to start receiving lead notifications.
                                </Typography>
                            </FormGroup>
                        </Grid>

                        {/* Existing numbers */}
                        {smsNotificationNumbers.map((item, index) => (
                            <Grid item xs={12} key={index}>
                                <Box display='flex' alignItems='center' style={{ gap: '1rem' }}>
                                    <Box display='flex' alignItems='center' style={{ gap: '0.5rem', minWidth: 180 }}>
                                        <OutlinedInput
                                            value='+353'
                                            disabled
                                            style={{ width: 70, color: '#8898aa', fontWeight: 500 }}
                                        />
                                        <Typography variant='body2' style={{ fontWeight: 500, fontSize: '0.875rem' }}>
                                            {item.phone.startsWith('+353') ? item.phone.substring(4) : item.phone}
                                        </Typography>
                                    </Box>
                                    <FormControl variant='filled' style={{ minWidth: 130 }}>
                                        <Select
                                            value={item.enabled ? 'Enabled' : 'Disabled'}
                                            onChange={(e) => toggleEnabled(index, e.target.value === 'Enabled')}
                                            input={<OutlinedInput />}
                                        >
                                            <MenuItem value='Disabled'>Disabled</MenuItem>
                                            <MenuItem value='Enabled'>Enabled</MenuItem>
                                        </Select>
                                    </FormControl>
                                    <IconButton
                                        onClick={() => removeNumber(index)}
                                        style={{ color: '#adb5bd' }}
                                        onMouseEnter={(e) => e.currentTarget.style.color = '#f44336'}
                                        onMouseLeave={(e) => e.currentTarget.style.color = '#adb5bd'}
                                    >
                                        <DeleteIcon />
                                    </IconButton>
                                </Box>
                            </Grid>
                        ))}

                        {smsNotificationNumbers.length === 0 && (
                            <Grid item xs={12}>
                                <Typography variant='body2' style={{ color: '#8898aa' }}>
                                    No notification numbers added yet.
                                </Typography>
                            </Grid>
                        )}

                        {/* SMS Preview */}
                        <Grid item xs={12}>
                            <Typography variant='h4' style={{ marginBottom: '1rem' }}>
                                SMS Preview
                            </Typography>
                            <Box
                                p={2}
                                borderRadius={8}
                                style={{
                                    backgroundColor: '#e8f5e9',
                                    border: '1px solid #a5d6a7',
                                    minHeight: 80
                                }}
                            >
                                <Typography variant='caption' style={{ fontWeight: 600, color: '#2e7d32', display: 'block', marginBottom: 8 }}>
                                    Notification — Sent when a new lead is assigned
                                </Typography>
                                <Typography variant='body2' style={{ color: '#1b5e20', whiteSpace: 'pre-wrap' }}>
                                    {smsPreview}
                                </Typography>
                                <Typography variant='caption' style={{ display: 'block', marginTop: 8, color: smsPreview.length > SMS_LIMIT ? '#d32f2f' : '#2e7d32' }}>
                                    {smsPreview.length}/{SMS_LIMIT} characters{smsPreview.length > SMS_LIMIT ? ' ⚠ Over limit — message will be cut off!' : ''}
                                </Typography>
                            </Box>
                        </Grid>

                        {/* No delivery method warning */}
                        {!hasDeliveryMethod && (
                            <Grid item xs={12}>
                                <Box
                                    p={2}
                                    borderRadius={4}
                                    style={{ backgroundColor: '#fff3cd', border: '1px solid #ffc107' }}
                                >
                                    <Typography variant='body2' style={{ fontWeight: 500, color: '#856404' }}>
                                        A delivery method (Email or CRM) must be set before SMS notifications can be enabled.
                                    </Typography>
                                </Box>
                            </Grid>
                        )}

                        {/* Save Button */}
                        <Grid item xs={12}>
                            <Box display='flex' justifyContent='flex-end' width='100%'>
                                <Button
                                    variant='contained'
                                    color='primary'
                                    disabled={loading}
                                    onClick={() => saveDetails()}
                                >
                                    {!loading ? (
                                        <>Save SMS Notifications</>
                                    ) : (
                                        <Loader height={12} />
                                    )}
                                </Button>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Collapse>
        </Card>
    )
}

export default SmsNotificationsCard
